import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import CookieBanner from "@/components/CookieBanner";
import SkipToContent from "@/components/SkipToContent";
import { useSettings } from "@/contexts/SettingsContext";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { BACKEND_URL } from "@/lib/api";

export default function PublicLayout({
  children,
  pageTitle,
  pageDescription,
  testid = "public-layout",
  ogImage,
}) {
  const { settings } = useSettings();
  const { locale } = useLocale();
  const location = useLocation();
  const baseName = settings?.business_name || "King's Café";
  const title = pageTitle ? `${pageTitle} — ${baseName}` : baseName;
  const description =
    pageDescription ||
    pickLocalized(settings?.tagline, locale, "Caffe Bar Kings, Mursko Središće.");
  const origin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  const pathname = location.pathname;
  // Canonical: bare URL for the default locale (hr), ?lang=xx suffix otherwise.
  const canonical = `${origin}${pathname}${locale === "hr" ? "" : `?lang=${locale}`}`;
  const heroImage = ogImage || settings?.hero_image_url || `${BACKEND_URL}/api/static/og-default.jpg`;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground" data-testid={testid}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <html lang={locale} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="hr" href={`${origin || ""}${pathname}`} />
        <link rel="alternate" hrefLang="en" href={`${origin || ""}${pathname}?lang=en`} />
        <link rel="alternate" hrefLang="x-default" href={`${origin || ""}${pathname}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content={baseName} />
        <meta property="og:locale" content={locale === "hr" ? "hr_HR" : "en_GB"} />
        {heroImage && <meta property="og:image" content={heroImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {heroImage && <meta name="twitter:image" content={heroImage} />}
        <meta name="theme-color" content="#0a0a0a" />
      </Helmet>
      <SkipToContent />
      <PublicNavbar />
      <main id="main" className="flex-1 focus:outline-none" tabIndex={-1}>
        {children}
      </main>
      <PublicFooter />
      <CookieBanner />
    </div>
  );
}
